import {
  BULK_ORDER_MAX_FILE_SIZE_BYTES,
  BULK_ORDER_MAX_ROWS,
  BULK_ORDER_REQUIRED_COLUMNS,
  BULK_ORDER_MANDATORY_ROW_FIELDS,
} from "./constants";

const isCsvFile = (file) => {
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return (
    name.endsWith(".csv") ||
    type === "text/csv" ||
    type === "application/vnd.ms-excel"
  );
};

const parseCsvLine = (line) => {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
};

const isBlankCsvLine = (line) => {
  const cells = parseCsvLine(line);
  return cells.every((cell) => !cell || String(cell).trim() === "");
};

const getCsvLines = (text) =>
  text
    .replace(/^\uFEFF/, "")
    .split(/\r\n|\n|\r/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
    .filter((line) => !isBlankCsvLine(line));

export const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = () => reject(new Error("Failed to read CSV file"));
    reader.readAsText(file);
  });

const buildCleanedCsvFile = (originalFile, lines) => {
  const content = `${lines.join("\n")}\n`;
  return new File([content], originalFile.name || "bulk-order.csv", {
    type: originalFile.type || "text/csv",
    lastModified: Date.now(),
  });
};

export const validateBulkOrderCsvFile = async (file) => {
  if (!file) {
    return { valid: false, error: "Please select a CSV file to upload." };
  }

  if (!isCsvFile(file)) {
    return { valid: false, error: "Only CSV files are allowed." };
  }

  if (file.size > BULK_ORDER_MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: "File size cannot be more than 5 MB.",
    };
  }

  let text;
  try {
    text = await readFileAsText(file);
  } catch (error) {
    return { valid: false, error: error.message };
  }

  // Drops blank lines and comma-only Excel leftover rows.
  const lines = getCsvLines(text);
  if (lines.length < 2) {
    return {
      valid: false,
      error: "CSV must include a header row and at least one data row.",
    };
  }

  // Ignore trailing empty header cells (common Excel export: `...,product_group_id,,,,,`).
  const headers = parseCsvLine(lines[0])
    .map((header) => header.trim().toLowerCase())
    .filter((header) => header.length > 0);
  const missingColumns = BULK_ORDER_REQUIRED_COLUMNS.filter(
    (column) => !headers.includes(column)
  );

  if (missingColumns.length > 0) {
    return {
      valid: false,
      error: `Missing required columns: ${missingColumns.join(", ")}`,
    };
  }

  const extraColumns = headers.filter(
    (header) => !BULK_ORDER_REQUIRED_COLUMNS.includes(header)
  );
  if (extraColumns.length > 0) {
    return {
      valid: false,
      error: `Unexpected columns found: ${extraColumns.join(", ")}`,
    };
  }

  if (headers.length !== BULK_ORDER_REQUIRED_COLUMNS.length) {
    return {
      valid: false,
      error: "CSV header columns do not match the required template.",
    };
  }

  const dataRowCount = lines.length - 1;
  if (dataRowCount > BULK_ORDER_MAX_ROWS) {
    return {
      valid: false,
      error: `CSV cannot have more than ${BULK_ORDER_MAX_ROWS} rows. Found ${dataRowCount}.`,
    };
  }

  const mandatoryIndexes = BULK_ORDER_MANDATORY_ROW_FIELDS.map((field) => ({
    field,
    index: headers.indexOf(field),
  })).filter((item) => item.index >= 0);

  for (let rowIndex = 1; rowIndex < lines.length; rowIndex += 1) {
    const cells = parseCsvLine(lines[rowIndex]);
    for (let i = 0; i < mandatoryIndexes.length; i += 1) {
      const { field, index } = mandatoryIndexes[i];
      const value = cells[index];
      if (value == null || String(value).trim() === "") {
        return {
          valid: false,
          error: `Row ${rowIndex}: "${field}" is required and cannot be empty.`,
        };
      }
    }
  }

  return {
    valid: true,
    rowCount: dataRowCount,
    headers,
    cleanedFile: buildCleanedCsvFile(file, lines),
  };
};
